import { ClientEntity } from 'src/app/models/clients';
import { GeneralSelectionFacade } from 'src/app/store/general-selection/facade/general-selection.facade';
import { InvestorService } from "src/app/services/http/investor.service";
import { ofType, Actions, createEffect } from "@ngrx/effects";
import { switchMap, map, catchError, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import * as SystemActions from '../../system/actions/system.actions';
import * as InvestorActions from './../actions/investor.actions';
import * as ErrorActions from '../../global-notification/actions/global-notification.action';
import { Injectable } from "@angular/core";
import { IBankAccount, IInvestorEntityDetails } from "src/app/models/investor";
import { BankAccountService } from 'src/app/services/http/bank-account.service';
import { deduplicatingSwitchMap } from 'src/app/utils/deduplicatingSwitchMap';

@Injectable()
export class InvestorEffects {

    constructor(
        private actions$: Actions,
        private investorService: InvestorService,
        private generalSelectionFacade: GeneralSelectionFacade,
        private bankAccountService: BankAccountService) {
    }

    getInvestorBankAccount$ = createEffect(() => this.actions$
        .pipe(
            ofType(InvestorActions.LOAD_INVESTOR_BANK_ACCOUNT),
            deduplicatingSwitchMap((action: InvestorActions.LoadInvestorBankAccount) => {
                return this.bankAccountService.getInvestorBankAccount(action.payload)
                    .pipe(
                        map((res: IBankAccount[]) => {
                            return new InvestorActions.LoadInvestorBankAccountSuccess(res);
                        }), catchError((err) => {
                            const error = (err && err.error_description) ? err.error_description : (err.error.error_description) ? err.error.error_description : 'unknown error';
                            return of(
                                new ErrorActions.NotificationErrorAction(error)
                            );
                        })
                    );
            })

        ));

    getInvestorEntityDetails$ = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(InvestorActions.LOAD_INVESTOR_ENTITY),
                deduplicatingSwitchMap((action: InvestorActions.LoadInvestorEntity) => {
                    return this.investorService.getInvestorDetails(<string>action.payload)
                        .pipe(
                            map((res: any) => {
                                if (!res) {
                                    return new ErrorActions.NotificationErrorAction('No Investor entity data');
                                }
                                const investorDetails: IInvestorEntityDetails = this.mapInvestorDetails(res);
                                return new InvestorActions.LoadInvestorEntitySuccess(investorDetails);
                            }), catchError((err) => {
                                return of(
                                    //Error notification changed as part of OB-3104 to prevent error notification displaying for managers with funds                         
                                    new SystemActions.UpdateStatus({ passed: false, text: "Unable to retrieve investor entity data" })
                                );
                            })
                        );
                })

            );
    });

    saveInvestorDetails$ = createEffect(() => {
        return this.actions$
            .pipe(
                ofType(InvestorActions.SAVE_INVESTOR_DETAILS),
                withLatestFrom(
                    this.generalSelectionFacade.getSelectedInvestorEntities()
                    // this.investorFacade.investorDetails$
                ),
                switchMap((v: [InvestorActions.SaveInvestorDetails, ClientEntity[]]) => {
                    const [action, clientEntities] = v;
                    if (clientEntities && clientEntities[0] && clientEntities[0].EntityId) {
                        const userEntityId = clientEntities[0].EntityId;
                        const investorEntityViewModel = {
                            ...action.payload
                        };
                        if (userEntityId) {
                            return this.investorService.saveInvestorDetails(investorEntityViewModel, userEntityId.toString()).pipe(
                                map((res: any) => {
                                    const investorDetails: IInvestorEntityDetails = this.mapInvestorDetails(res);
                                    return new InvestorActions.SaveInvestorDetailsSuccess(investorDetails);
                                }),
                                catchError((err) => {
                                    const error = (err && err.error_description) ? err.error_description : (err.error.error_description) ? err.error.error_description : 'saving investor details failed';
                                    return of(
                                        new InvestorActions.SaveInvestorDetailsFail(error),
                                        new ErrorActions.NotificationErrorAction(error)
                                    );
                                })
                            );
                        }
                    }
                })
            );
    });

    getInvestorEntityAdvisorInformation$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InvestorActions.LOAD_INVESTOR_ADVISOR_INFORMATION),
            deduplicatingSwitchMap((action: InvestorActions.LoadInvestorAdvisorInformation) => {
                return this.investorService.getInvestorsAdvisorInformation(action.payload)
                .pipe(
                    map((res: any) => {
                        if (!res) {
                            return new ErrorActions.NotificationErrorAction('Unable to retrieve advisor information');
                        }
                        
                        return new InvestorActions.LoadInvestorAdvisorInformationSuccess(res);                  
                    }), 
                    catchError((err) => {
                        return of(                         
                            new SystemActions.UpdateStatus({ passed: false, text: "Unable to retrieve investor advisor information" })
                        );
                    })
                );
            })
        );
    });


    private mapInvestorDetails(res: any): IInvestorEntityDetails {
        return {
            bankAccount: res.bankAccounts.map((b) => {
                return {
                    investorEntityId: res.entityId,
                    bankId: b.bankAccountId,
                    bankName: b.bankName,
                    bankAccountName: b.bankAccountName,
                    bankAccountNumber: b.bankAccountNumber,
                    isDefault: b.isDefault,
                    reference: b.swift,
                    isVerified: b.isVerified
                };
            }),
            taxDetails: {
                countryTaxResidency: res.countryOfResidence,
                marginalTaxRate: res.marginalTaxRate,
                taxRate: res.taxRate,
                irdNumber: res.irdNumber,
                taxResidentOverseas: res.nrwt,
                rwtExempt: res.rwtExempt,
                ail: res.ail,
                foreignTaxResidences: []
            },
            investorDetails: {
                investorEntityId: res.entityId,
                legalEntityType: res.legalType,
                name: res.entityName,
                addresses: null, // ignore, irrelevant for now
                reinvestmentOption: '',
                subType: res.subType
            }
        };
    }
}
